"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollapsedContext = exports.useTreeSelectFieldProvider = exports.TreeSelectField = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = __importStar(require("react"));
const react_aria_1 = require("react-aria");
const react_stately_1 = require("react-stately");
const components_1 = require("../../components");
const internal_1 = require("../../components/internal");
const PresentationContext_1 = require("../../components/PresentationContext");
const Css_1 = require("../../Css");
const ComboBoxInput_1 = require("../internal/ComboBoxInput");
const ListBox_1 = require("../internal/ListBox");
const utils_1 = require("./utils");
const Value_1 = require("../Value");
const utils_2 = require("../utils");
function TreeSelectField(props) {
    const { getOptionValue = (opt) => opt.id, // if unset, assume O implements HasId
    getOptionLabel = (opt) => opt.name, // if unset, assume O implements HasName
    options, onSelect, values, defaultCollapsed = false, ...otherProps } = props;
    const [collapsedKeys, setCollapsedKeys] = (0, react_1.useState)(Array.isArray(options) && defaultCollapsed ? options.map((o) => getOptionValue(o)) : []);
    const contextValue = (0, react_1.useMemo)(() => ({ collapsedKeys, setCollapsedKeys, getOptionValue }), 
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collapsedKeys, setCollapsedKeys]);
    return ((0, jsx_runtime_1.jsx)(exports.CollapsedContext.Provider, { value: contextValue, children: (0, jsx_runtime_1.jsx)(TreeSelectFieldBase, { ...otherProps, options: options, getOptionLabel: getOptionLabel, getOptionValue: getOptionValue, values: values, onSelect: ({ all, leaf, root }) => {
                onSelect({ all, leaf, root });
            } }) }));
}
exports.TreeSelectField = TreeSelectField;
function useTreeSelectFieldProvider() {
    return (0, react_1.useContext)(exports.CollapsedContext);
}
exports.useTreeSelectFieldProvider = useTreeSelectFieldProvider;
// create the context to hold the collapsed state, default should be false
exports.CollapsedContext = react_1.default.createContext({
    collapsedKeys: [],
    setCollapsedKeys: () => { },
    getOptionValue: () => ({}),
});
function TreeSelectFieldBase(props) {
    var _a, _b;
    const { fieldProps } = (0, PresentationContext_1.usePresentationContext)();
    const { values, options, getOptionValue, getOptionLabel, getOptionMenuLabel = getOptionLabel, disabled, readOnly, labelStyle, borderless, contrast = false, nothingSelectedText = "", onSelect, defaultCollapsed = false, placeholder, fullWidth = (_a = fieldProps === null || fieldProps === void 0 ? void 0 : fieldProps.fullWidth) !== null && _a !== void 0 ? _a : false, ...otherProps } = props;
    const isDisabled = !!disabled;
    const isReadOnly = !!readOnly;
    const initialOptions = Array.isArray(options) ? options : options.current;
    const { contains } = (0, react_aria_1.useFilter)({ sensitivity: "base" });
    const { collapsedKeys } = useTreeSelectFieldProvider();
    function levelOptions(o, level, filtering) {
        var _a;
        // If a user is filtering, then do not provide level to the options as the various paddings may look quite odd.
        const actualLevel = filtering ? 0 : level;
        return [
            [o, actualLevel],
            ...(((_a = o.children) === null || _a === void 0 ? void 0 : _a.length) && !collapsedKeys.includes((0, Value_1.valueToKey)(getOptionValue(o)))
                ? o.children.flatMap((oc) => levelOptions(oc, actualLevel + 1, filtering))
                : []),
        ];
    }
    // Initialize the TreeFieldState
    const [fieldState, setFieldState] = (0, react_1.useState)(() => {
        var _a;
        const filteredOptions = initialOptions.flatMap((o) => levelOptions(o, 0));
        const selectedOptions = (_a = values === null || values === void 0 ? void 0 : values.flatMap((v) => {
            var _a, _b;
            const maybeOption = (0, utils_1.findOption)(initialOptions, (0, Value_1.valueToKey)(v), getOptionValue);
            if (!maybeOption)
                return [];
            const { option } = maybeOption;
            // If the selected key has children then all children should also be considered selected.
            return [option, ...((_b = (_a = option.children) === null || _a === void 0 ? void 0 : _a.flatMap(utils_1.flattenOptions)) !== null && _b !== void 0 ? _b : [])];
        })) !== null && _a !== void 0 ? _a : [];
        const selectedKeys = selectedOptions.map((o) => (0, Value_1.valueToKey)(getOptionValue(o)));
        // It is possible that all the children of a parent were considered selected `values`, but the parent wasn't.
        // In this case, the parent also should be considered a selected option.
        function areAllChildrenSelected(maybeParent) {
            const isSelected = selectedKeys.includes((0, Value_1.valueToKey)(getOptionValue(maybeParent)));
            // if this key is already selected, then return true
            if (isSelected)
                return true;
            // If we do not have children, then return the state of this leaf node.
            if (!maybeParent.children || maybeParent.children.length === 0)
                return isSelected;
            // If we do have children, then check if all children are selected.
            // if all are selected, then push this parent to the selectedKeys and selectedOptions
            const areAllSelected = maybeParent.children.every(areAllChildrenSelected);
            if (areAllSelected) {
                selectedKeys.push((0, Value_1.valueToKey)(getOptionValue(maybeParent)));
                selectedOptions.push(maybeParent);
            }
            return areAllSelected;
        }
        initialOptions.forEach(areAllChildrenSelected);
        // Given a child option, determine if the parent is selected.
        const isParentSelected = (option) => {
            var _a;
            const parents = (_a = (0, utils_1.findOption)(initialOptions, (0, Value_1.valueToKey)(getOptionValue(option)), getOptionValue)) === null || _a === void 0 ? void 0 : _a.parents;
            if (!parents)
                return false;
            return parents.some((parent) => selectedKeys.includes((0, Value_1.valueToKey)(getOptionValue(parent))));
        };
        const selectedOptionsLabels = selectedOptions.filter((o) => !isParentSelected(o)).map(getOptionLabel);
        return {
            selectedKeys,
            inputValue: selectedOptions.length === 1
                ? getOptionLabel(selectedOptions[0])
                : isReadOnly && selectedOptions.length > 0
                    ? selectedOptionsLabels.join(", ")
                    : selectedOptions.length === 0
                        ? nothingSelectedText
                        : "",
            filteredOptions,
            selectedOptions,
            allOptions: initialOptions,
            selectedOptionsLabels,
            optionsLoading: false,
            allowCollapsing: true,
        };
    });
    // React to collapsed keys and update the filtered options
    const reactToCollapse = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        // Do not run this effect on first render. Otherwise we'd be triggering a re-render on first render.
        if (reactToCollapse.current) {
            setFieldState(({ allOptions, inputValue, ...others }) => ({
                allOptions,
                inputValue,
                ...others,
                filteredOptions: allOptions.flatMap((o) => levelOptions(o, 0, inputValue.length > 0).filter(([option]) => contains(getOptionLabel(option), inputValue))),
            }));
        }
        reactToCollapse.current = true;
    }, 
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collapsedKeys]);
    // Update the filtered options when the input value changes
    const onInputChange = (0, react_1.useCallback)((inputValue) => {
        setFieldState((prevState) => {
            return {
                ...prevState,
                inputValue,
                allowCollapsing: inputValue.length === 0,
                filteredOptions: prevState.allOptions.flatMap((o) => levelOptions(o, 0, inputValue.length > 0).filter(([option]) => contains(getOptionLabel(option), inputValue))),
            };
        });
    }, 
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFieldState]);
    // Handle loading of the options in the case that they are loaded via a Promise.
    const maybeInitLoad = (0, react_1.useCallback)(async (options, fieldState, setFieldState) => {
        if (!Array.isArray(options)) {
            setFieldState((prevState) => ({ ...prevState, optionsLoading: true }));
            const loadedOptions = (await options.load()).options;
            const filteredOptions = loadedOptions.flatMap((o) => levelOptions(o, 0, fieldState.inputValue.length > 0).filter(([option]) => contains(getOptionLabel(option), fieldState.inputValue)));
            // Ensure the `unset` option is prepended to the top of the list if `unsetLabel` was provided
            setFieldState((prevState) => ({
                ...prevState,
                filteredOptions,
                allOptions: loadedOptions,
                optionsLoading: false,
            }));
        }
    }, 
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    // Only on the first open of the listbox, we want to load the options if they haven't been loaded yet.
    const firstOpen = (0, react_1.useRef)(true);
    function onOpenChange(isOpen) {
        if (firstOpen.current && isOpen) {
            // TODO: verify this eslint ignore
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            maybeInitLoad(options, fieldState, setFieldState);
            firstOpen.current = false;
        }
        if (isOpen) {
            // reset the input field to allow the user to start typing to filter
            setFieldState((prevState) => ({
                ...prevState,
                inputValue: "",
                filteredOptions: initialOptions.flatMap((o) => levelOptions(o, 0)),
            }));
        }
    }
    // This is _always_ going to appear new. Maybe `useMemo`?
    const comboBoxProps = {
        ...otherProps,
        placeholder: !values || values.length === 0 ? placeholder : "",
        label: props.label,
        inputValue: fieldState.inputValue,
        // where we might want to do flatmap and return diff kind of array (children ? add level prop) inside children callback - can put markup wrapper div adds padding
        // so we're not doing it multiple places
        items: fieldState.filteredOptions,
        isDisabled,
        isReadOnly,
        onInputChange,
        onOpenChange,
        children: ([item]) => (
        // what we're telling it to render. look at padding here - dont have to pass down to tree option - filtered options is where we're flat mapping
        (0, jsx_runtime_1.jsx)(react_stately_1.Item, { textValue: getOptionLabel(item), children: getOptionMenuLabel(item) }, (0, Value_1.valueToKey)(getOptionValue(item)))),
    };
    const state = (0, react_stately_1.useComboBoxState)({
        ...comboBoxProps,
        allowsEmptyCollection: true,
        allowsCustomValue: true,
    });
    // @ts-ignore - `selectionManager.state` exists, but not according to the types. We are tricking the ComboBox state to support multiple selections.
    state.selectionManager.state = (0, react_stately_1.useMultipleSelectionState)({
        selectionMode: "multiple",
        selectedKeys: fieldState.selectedKeys,
        onSelectionChange: (newKeys) => {
            if (newKeys === "all") {
                // We do not support an "All" option
                return;
            }
            // First figure out which keys changed so we can correctly determine which affiliated options may need to be updated as well.
            const existingKeys = state.selectionManager.selectedKeys;
            const addedKeys = new Set([...newKeys].filter((x) => !existingKeys.has(x)));
            const removedKeys = new Set([...existingKeys].filter((x) => !newKeys.has(x)));
            // Make sure there are changes before we do anything.
            if (addedKeys.size > 0 || removedKeys.size > 0) {
                // Quickly return out of this if all selections are removed
                if (newKeys.size === 0) {
                    setFieldState((prevState) => ({
                        ...prevState,
                        inputValue: nothingSelectedText,
                        selectedKeys: [],
                        selectedOptions: [],
                    }));
                    onSelect({
                        all: { values: [], options: [] },
                        leaf: { values: [], options: [] },
                        root: { values: [], options: [] },
                    });
                    return;
                }
                // `onSelectionChange` is only ever going to be adding or removing 1 key at a time.
                // The below logic for adding/removing using a forEach loop is just to make TS happy.
                // This may look like a lot of logic, but in the execution it will only ever be adding/removing 1 key at a time, so it really isn't as bad as it looks.
                // For added keys, we need to see if any other options should be added as well.
                [...addedKeys].forEach((key) => {
                    var _a;
                    const maybeOption = (0, utils_1.findOption)(fieldState.allOptions, key, getOptionValue);
                    if (!maybeOption)
                        return;
                    const { option, parents } = maybeOption;
                    // If the newly added option has children, then consider the children to be newly added keys as well.
                    if (option && option.children && option.children.length > 0) {
                        const childrenKeys = option.children.flatMap(utils_1.flattenOptions).map((o) => (0, Value_1.valueToKey)(getOptionValue(o)));
                        [key, ...childrenKeys].forEach(addedKeys.add, addedKeys);
                    }
                    // If this was a child that was selected, then see if every other child is also selected, and if so, consider the parent selected.
                    // Walk up the parents and determine their state.
                    for (const parent of parents.reverse()) {
                        const allChecked = (_a = parent.children) === null || _a === void 0 ? void 0 : _a.every((child) => {
                            const childKey = (0, Value_1.valueToKey)(getOptionValue(child));
                            return addedKeys.has(childKey) || existingKeys.has(childKey);
                        });
                        if (allChecked) {
                            addedKeys.add((0, Value_1.valueToKey)(getOptionValue(parent)));
                        }
                    }
                });
                // For removed keys, we need to also unselect any children and parents of the removed key
                [...removedKeys].forEach((key) => {
                    // Grab the option and parents of the option that was removed
                    const maybeOption = (0, utils_1.findOption)(fieldState.allOptions, key, getOptionValue);
                    if (!maybeOption)
                        return;
                    const { option, parents } = maybeOption;
                    // If the option has children, then we need to unselect those children as well
                    if (option.children && option.children.length > 0) {
                        const childrenKeys = option.children.flatMap(utils_1.flattenOptions).map((o) => (0, Value_1.valueToKey)(getOptionValue(o)));
                        [key, ...childrenKeys].forEach(removedKeys.add, removedKeys);
                    }
                    // If the option has parents, then we need to unselect those parents as well
                    if (parents.length > 0) {
                        const parentKeys = parents.map((o) => (0, Value_1.valueToKey)(getOptionValue(o)));
                        [key, ...parentKeys].forEach(removedKeys.add, removedKeys);
                    }
                });
                // Create the lists to update our TreeState with
                const selectedKeys = new Set([...existingKeys, ...addedKeys].filter((x) => !removedKeys.has(x)));
                const selectedOptions = fieldState.allOptions
                    .flatMap(utils_1.flattenOptions)
                    .filter((o) => selectedKeys.has((0, Value_1.valueToKey)(getOptionValue(o))));
                // Our `onSelect` callback provides three things:
                // 1. ALL selected values + options
                // 2. Only the top level selected values + options
                // 3. Only the leaf selected values + options
                // For the top level and leaf selections, we need to do some extra work to determine which options are the top level and leaf.
                function getTopLevelSelections(o) {
                    // If this element is already selected, return early. Do not bother looking through children.
                    if (selectedKeys.has((0, Value_1.valueToKey)(getOptionValue(o))))
                        return [o];
                    // If this element has no children, then look through the children for top level selected options.
                    if (o.children)
                        return [...o.children.flatMap(getTopLevelSelections)];
                    return [];
                }
                const rootOptions = fieldState.allOptions.flatMap(getTopLevelSelections);
                const rootValues = rootOptions.map(getOptionValue);
                // Finally get the list of options that are just the "leaf" options, meaning they have no children.
                const leafOptions = selectedOptions.filter((o) => !o.children || o.children.length === 0);
                const leafValues = leafOptions.map(getOptionValue);
                setFieldState((prevState) => ({
                    ...prevState,
                    inputValue: nothingSelectedText,
                    filteredOptions: initialOptions.flatMap((o) => levelOptions(o, 0)),
                    selectedKeys: [...selectedKeys],
                    selectedOptions,
                    selectedOptionsLabels: rootOptions.map(getOptionLabel),
                }));
                onSelect({
                    all: { values: [...selectedKeys].map((key) => (0, Value_1.keyToValue)(key)), options: selectedOptions },
                    leaf: { values: leafValues, options: leafOptions },
                    root: { values: rootValues, options: rootOptions },
                });
            }
        },
    });
    // Resets the TreeFieldState when the 'blur' event is triggered on the input.
    function resetField() {
        const { inputValue, selectedOptions } = fieldState;
        if (inputValue !== nothingSelectedText ||
            (selectedOptions.length === 1 && inputValue !== getOptionLabel(selectedOptions[0]))) {
            setFieldState((prevState) => ({
                ...prevState,
                inputValue: selectedOptions.length === 1
                    ? getOptionLabel(selectedOptions[0])
                    : selectedOptions.length === 0
                        ? nothingSelectedText
                        : "",
                filteredOptions: initialOptions.flatMap((o) => levelOptions(o, 0)),
                allowCollapsing: true,
            }));
        }
    }
    const comboBoxRef = (0, react_1.useRef)(null);
    const triggerRef = (0, react_1.useRef)(null);
    const inputRef = (0, react_1.useRef)(null);
    const inputWrapRef = (0, react_1.useRef)(null);
    const listBoxRef = (0, react_1.useRef)(null);
    const popoverRef = (0, react_1.useRef)(null);
    const { buttonProps: triggerProps, inputProps, listBoxProps, labelProps, } = (0, react_aria_1.useComboBox)({
        ...comboBoxProps,
        inputRef,
        buttonRef: triggerRef,
        listBoxRef,
        popoverRef,
    }, state);
    const { buttonProps } = (0, react_aria_1.useButton)({ ...triggerProps, isDisabled: isDisabled || isReadOnly }, triggerRef);
    // useOverlayPosition moves the overlay to the top of the DOM to avoid any z-index issues. Uses the `targetRef` to DOM placement
    const { overlayProps: positionProps } = (0, react_aria_1.useOverlayPosition)({
        targetRef: inputWrapRef,
        overlayRef: popoverRef,
        scrollRef: listBoxRef,
        shouldFlip: true,
        isOpen: state.isOpen,
        onClose: state.close,
        placement: "bottom left",
        offset: borderless ? 8 : 4,
    });
    positionProps.style = {
        ...positionProps.style,
        width: (_b = comboBoxRef === null || comboBoxRef === void 0 ? void 0 : comboBoxRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth,
        // Ensures the menu never gets too small.
        minWidth: 200,
    };
    const fieldMaxWidth = (0, utils_2.getFieldWidth)(fullWidth);
    return ((0, jsx_runtime_1.jsxs)("div", { css: Css_1.Css.df.fdc.w100.maxw(fieldMaxWidth).if(labelStyle === "left").maxw100.$, ref: comboBoxRef, children: [(0, jsx_runtime_1.jsx)(ComboBoxInput_1.ComboBoxInput, { ...otherProps, fullWidth: fullWidth, labelStyle: labelStyle, buttonProps: buttonProps, buttonRef: triggerRef, inputProps: inputProps, inputRef: inputRef, inputWrapRef: inputWrapRef, listBoxRef: listBoxRef, state: state, labelProps: labelProps, selectedOptions: fieldState.selectedOptions, selectedOptionsLabels: fieldState.selectedOptionsLabels, getOptionValue: getOptionValue, getOptionLabel: getOptionLabel, contrast: contrast, borderless: borderless, tooltip: (0, components_1.resolveTooltip)(disabled, undefined, readOnly), resetField: resetField, nothingSelectedText: nothingSelectedText, isTree: true }), state.isOpen && ((0, jsx_runtime_1.jsx)(internal_1.Popover, { triggerRef: triggerRef, popoverRef: popoverRef, positionProps: positionProps, onClose: () => state.close(), isOpen: state.isOpen, minWidth: 200, children: (0, jsx_runtime_1.jsx)(ListBox_1.ListBox, { ...listBoxProps, positionProps: positionProps, state: state, listBoxRef: listBoxRef, selectedOptions: fieldState.selectedOptions, getOptionLabel: getOptionLabel, getOptionValue: (o) => (0, Value_1.valueToKey)(getOptionValue(o)), contrast: contrast, horizontalLayout: labelStyle === "left", loading: fieldState.optionsLoading, allowCollapsing: fieldState.allowCollapsing, isTree: true }) }))] }));
}
